import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { Plus, Trash2, KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function UsersPage() {
  const { t } = useLang();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    email: "", password: "", name: "", role: "chercheur", site: "Geneva",
  });

  const load = async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/users", form);
      toast.success("Utilisateur créé");
      setOpen(false);
      setForm({ email: "", password: "", name: "", role: "chercheur", site: "Geneva" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggleDisabled = async (u) => {
    await api.patch(`/users/${u._id}`, { disabled: !u.disabled });
    load();
  };

  const del = async (u) => {
    if (u.email === user.email) return toast.error("Impossible de supprimer votre propre compte");
    if (!window.confirm(`Supprimer ${u.email} ?`)) return;
    await api.delete(`/users/${u._id}`);
    load();
  };

  const resetMfa = async (u) => {
    if (!window.confirm(`Réinitialiser la MFA de ${u.email} ? Son ancien QR ne fonctionnera plus.`)) return;
    try {
      await api.post(`/users/${u._id}/reset-mfa`);
      toast.success(`MFA de ${u.email} réinitialisée`);
      load();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/40">
            {t("users")}
          </div>
          <h1 className="text-3xl font-semibold tracking-tight mt-1">Gestion des identités</h1>
        </div>
        <Button data-testid="new-user-btn" onClick={() => setOpen(true)}
                className="bg-cyan-500 text-black hover:bg-cyan-400">
          <Plus className="h-4 w-4" /> Nouvel utilisateur
        </Button>
      </div>

      <Card className="bg-white/[0.02] border-white/10 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b border-white/10 bg-white/[0.02]">
            <tr className="text-left text-white/50 uppercase tracking-[0.2em] text-[10px]">
              <th className="p-3">Email</th>
              <th className="p-3">Nom</th>
              <th className="p-3">Rôle</th>
              <th className="p-3">Site</th>
              <th className="p-3">MFA</th>
              <th className="p-3">Statut</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u._id} className="border-b border-white/5 hover:bg-white/[0.02]"
                  data-testid={`user-row-${u._id}`}>
                <td className="p-3 font-mono text-xs text-white/80">{u.email}</td>
                <td className="p-3">{u.name}</td>
                <td className="p-3"><StatusBadge status="info">{u.role}</StatusBadge></td>
                <td className="p-3 text-white/60">{u.site}</td>
                <td className="p-3">
                  <StatusBadge status={u.totp_enabled ? "trusted" : "warning"}>
                    {u.totp_enabled ? "on" : "off"}
                  </StatusBadge>
                </td>
                <td className="p-3">
                  <Switch checked={!u.disabled} onCheckedChange={() => toggleDisabled(u)}
                          data-testid={`toggle-${u._id}`} />
                </td>
                <td className="p-3 text-right">
                  <div className="flex gap-1 justify-end">
                    <Button variant="ghost" size="icon" onClick={() => resetMfa(u)}
                            data-testid={`reset-mfa-${u._id}`}
                            title="Réinitialiser la MFA"
                            className="text-white/60 hover:text-orange-400 hover:bg-white/5 h-8 w-8">
                      <KeyRound className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => del(u)}
                            data-testid={`delete-${u._id}`}
                            className="text-white/60 hover:text-rose-400 hover:bg-white/5 h-8 w-8">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white">
          <DialogHeader><DialogTitle>Nouvel utilisateur</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Nom">
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     data-testid="new-user-name" className="bg-white/[0.03] border-white/10" />
            </Field>
            <Field label="Email">
              <Input type="email" value={form.email}
                     onChange={(e) => setForm({ ...form, email: e.target.value })}
                     data-testid="new-user-email" className="bg-white/[0.03] border-white/10" />
            </Field>
            <Field label="Mot de passe">
              <Input type="password" value={form.password}
                     onChange={(e) => setForm({ ...form, password: e.target.value })}
                     data-testid="new-user-password" className="bg-white/[0.03] border-white/10" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Rôle">
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="new-user-role" className="bg-white/[0.03] border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="chercheur">Chercheur</SelectItem>
                    <SelectItem value="medecin">Médecin</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="auditeur">Auditeur</SelectItem>
                    <SelectItem value="partenaire">Partenaire</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Site">
                <Select value={form.site} onValueChange={(v) => setForm({ ...form, site: v })}>
                  <SelectTrigger data-testid="new-user-site" className="bg-white/[0.03] border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Geneva">Genève</SelectItem>
                    <SelectItem value="Nairobi">Nairobi</SelectItem>
                    <SelectItem value="Kinshasa">Kinshasa (RDC)</SelectItem>
                    <SelectItem value="Rio">Rio</SelectItem>
                    <SelectItem value="New Delhi">New Delhi</SelectItem>
                    <SelectItem value="Kuala Lumpur">Kuala Lumpur</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Button onClick={create} data-testid="create-user-btn"
                    className="bg-cyan-500 text-black hover:bg-cyan-400 w-full">
              Créer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-[10px] uppercase tracking-[0.2em] text-white/60">{label}</Label>
      {children}
    </div>
  );
}
